import { ChangeEvent, useEffect, useMemo, useState } from 'react';

import { analytics } from '@sureapp/wl-toggle-common/dist/utils';
import clsx from 'clsx';
import { sortBy } from 'lodash';
import { Helmet } from 'react-helmet';

import LayoutSelfService from '@@/common/components/LayoutSelfService';
import Toggle from '@@/common/components/Toggle';
import { useCmsContentAndHelpers } from '@@/common/hooks/useCmsContent';
import { useTitleForPartner } from '@@/common/hooks/useTitleForPartner';
import { isCmsError } from '@@/common/services/typeGuards';

import imgPeeps from './peeps@3x.png';
import imgSofa from './sofa@3x.png';
import './AboutUsView.css';

type TTeam = {
  bioNow?: string;
  bioThen?: string;
  id?: string;
  img?: string;
  name?: string;
  order?: number | string;
};

function AboutUsView() {
  const { getTitle } = useTitleForPartner();
  const { content, getEntryFieldBySlug } = useCmsContentAndHelpers(
    'about-us-team-cards'
  );

  const teamCardsConfig: TTeam[] = useMemo(() => {
    if (isCmsError(content)) {
      return [];
    }
    return sortBy(
      content?.entries?.map((teamMember) => ({
        bioNow: getEntryFieldBySlug(teamMember.slug, 'bio-now')?.text,
        bioThen: getEntryFieldBySlug(teamMember.slug, 'bio-then')?.text,
        id: teamMember.id,
        img: getEntryFieldBySlug(teamMember.slug, 'card-image')?.text,
        name: getEntryFieldBySlug(teamMember.slug, 'team-member-name')?.text,
        order: Number(getEntryFieldBySlug(teamMember.slug, 'order')?.text),
      })) ?? [],
      ['order']
    );
  }, [content, getEntryFieldBySlug]);

  const [teamToggles, setTeamToggles] = useState<{
    [id: string]: boolean | undefined;
  }>({});

  useEffect(function onMount() {
    analytics.page();
  }, []);

  function handleToggleChange(e: ChangeEvent<HTMLInputElement>) {
    setTeamToggles({
      ...teamToggles,
      [e.target.name]: e.target.checked,
    });
  }

  return (
    <LayoutSelfService
      className="AboutUsView"
      header="At Toggle, we hit the refresh button on renters insurance."
      backgroundImgSrc={null}
      isLayoutContainer
      overrideClasses={{
        header_greeting: 'AboutUsView-header-greeting',
      }}
    >
      <Helmet>
        <title>{getTitle('About Us | Toggle Insurance')}</title>
        <meta
          name="description"
          content="Covered. Anytime. Anywhere. Learn about what makes our renters insurance subscriptions unique."
        />
      </Helmet>
      <div className="AboutUsView-section AboutUsView-section--mission">
        <div className="AboutUsView-container">
          <div className="AboutUsView-content">
            <h1 className="AboutUsView-heading">Our mission</h1>
            <p className="AboutUsView-tagline">
              To make renters insurance make sense for everyone.
            </p>
            <p className="AboutUsView-info">
              We give you complete control every step of the way, from covering
              your stuff anytime and anywhere to having your back as you reach
              for the next rung on the financial ladder.
            </p>
            <p className="AboutUsView-info">
              We make insurance easier to understand and more flexible to keep
              up with your ever-changing life.
            </p>
          </div>
        </div>
        <img
          src={imgPeeps}
          alt="Person taking laptop out of bookbag"
          className="AboutUsView-img"
        />
      </div>

      <div className="AboutUsView-section AboutUsView-section--story">
        <div className="AboutUsView-container">
          <div className="AboutUsView-content">
            <h2 className="AboutUsView-heading">Our story</h2>
            <p className="AboutUsView-tagline">
              We don’t act like your typical insurance company, because we’re
              not.
            </p>
            <p className="AboutUsView-info">
              But insurance is deep in our DNA-because we&apos;re a member of
              the Farmers
              <sup>&reg;</sup> Insurance family. Which means when you need us,
              we&apos;re there. And with over 90 years of experience, we&apos;re
              not fly by night.
            </p>
          </div>
        </div>
        <img
          src={imgSofa}
          alt="Sofa with lamp on a side table"
          className="AboutUsView-img"
        />
      </div>

      <div className="AboutUsView-section AboutUsView-section--team">
        <div className="AboutUsView-container">
          <div className="AboutUsView-content">
            <h2 className="AboutUsView-heading">Our team</h2>
            <p className="AboutUsView-tagline">We were born to Toggle.</p>
            <p className="AboutUsView-info">
              We think you&apos;ll like being part of the Toggle family. We take
              insurance seriously, but we don&apos;t take ourselves too
              seriously. When we aren&apos;t figuring out smarter ways to insure
              renters like you, we like to do stuff like knit, practice mixed
              martial arts, fly fish, craft lattes, play video games and more.
            </p>
            <p className="AboutUsView-info">
              Want to learn more about what makes us tick or what we looked like
              before we were insurance ninjas? Just toggle our photos and get a
              glimpse behind the curtain.
            </p>
          </div>
        </div>
        <ul className="AboutUsView-teamList">
          {teamCardsConfig.map((person) => {
            const isChecked = person?.id ? teamToggles[person.id] : false;
            return (
              <li
                className={clsx('AboutUsView-teamCard', {
                  'is-toggled': isChecked,
                })}
                key={person.id}
                style={{
                  backgroundImage: `url(${person.img})`,
                }}
                data-testid="team-card"
              >
                <h4 className="AboutUsView-teamCard-heading">{person.name}</h4>
                <p className="AboutUsView-teamCard-bio">
                  {isChecked ? person.bioThen : person.bioNow}
                </p>
                <Toggle
                  name={person.id}
                  className="AboutUsView-teamCard-toggle"
                  checked={isChecked}
                  checkedLabel="Then"
                  uncheckedLabel="Now"
                  size="md"
                  onChange={handleToggleChange}
                />
              </li>
            );
          })}
        </ul>
      </div>

      <div className="AboutUsView-section AboutUsView-section--collab">
        <div className="AboutUsView-container">
          <h3 className="AboutUsView-heading">
            Interested in teaming up? Cool. Let&apos;s talk.
          </h3>
          <p className="AboutUsView-info">
            Let&apos;s put our thinking to work, together. If you like what you
            see and think you&apos;ve got the stuff it takes to make a business
            marriage work, let&apos;s talk.
          </p>
          <a
            href="mailto:letscollaborate@gettoggle.com"
            className="AboutUsView-collabCta btn btn-secondary btn-success btn-block"
          >
            Let&apos;s team up
          </a>
        </div>
      </div>
    </LayoutSelfService>
  );
}

export default AboutUsView;
